import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationService } from '@app/_shared/_services/authentication.service';
import { RedirectService } from '@app/_shared/_services/redirect.service';
import { environment } from '@environments/environment';
import { first } from 'rxjs/operators';
import { alert } from 'devextreme/ui/dialog';

@Component({
  selector: 'auth-formverify',
  templateUrl: './formverify.component.html',
  styleUrls: ['./formverify.component.scss']
})
export class FormverifyComponent implements OnInit {

  // Form for requesting activation email
  requestForm: UntypedFormGroup;

  loading = false;
  submitted = false;
  error = '';
  success = '';
  isAlreadyActive = false;
  environment = environment;

  // User info from token verification
  user: any;
  token: string;

  // State: 'request' = show email form, 'activate' = show activation confirmation
  mode: 'request' | 'activate' = 'request';

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private redirectService: RedirectService
  ) { }

  ngOnInit(): void {
    // Check if we have a token in the URL (user clicked activation link)
    const username = this.route.snapshot.queryParams.u;
    this.token = this.route.snapshot.queryParams.token;
    const redirectUrl = this.route.snapshot.queryParams.redirect;

    if (this.token && redirectUrl) {
      // Token + redirect = activation transparente (depuis mail d'information avec boutons)
      this.mode = 'activate';
      this.loading = true;
      this.autoActivateAndRedirect(redirectUrl);
    } else if (this.token) {
      // Token seul = activer et afficher message de confirmation
      // sendConfirmationEmail = true (pas de redirect = vient du mail d'activation)
      this.mode = 'activate';
      this.loading = true;
      this.activateAndShowConfirmation();
    } else {
      // No token - show form to request activation email
      this.mode = 'request';
      this.requestForm = this.formBuilder.group({
        username: [username || '', Validators.required]
      });
    }
  }

  /**
   * Activation transparente : active le compte et redirige immédiatement
   * Connecte l'utilisateur sur Angular + PHP puis redirige
   * sendConfirmationEmail = false car vient du mail d'information (pas besoin de mail de confirmation)
   *
   * SÉCURITÉ : L'auto-login n'est fait que si l'API retourne SUCCESS (token valide)
   * Si le token est invalide, on affiche un message d'erreur (pas d'auto-login)
   */
  private autoActivateAndRedirect(redirectUrl: string): void {
    const username = this.route.snapshot.queryParams.u;
    this.authenticationService.activateAccount(this.token, false)
      .pipe(first())
      .subscribe({
        next: (res) => {
          // Token valide - connecter sur PHP via login.php puis rediriger
          // Fonctionne pour account_activated ET already_active (API retourne success dans les 2 cas)
          this.loginPhpAndRedirect(res.user?.email || username, redirectUrl);
        },
        error: (error) => {
          // Token invalide ou erreur - NE PAS faire d'auto-login (sécurité)
          this.loading = false;
          const msg = error?.msg || error?.message || (typeof error === 'string' ? error : '');
          if (msg === 'invalid_token') {
            this.error = 'Ce lien n\'est plus valide ou a expiré.';
          } else {
            // En cas d'erreur inconnue, rediriger sans auto-login
            this.redirectTo(redirectUrl);
          }
        }
      });
  }

  /**
   * Active le compte et redirige vers mon-espace
   * Connecte l'utilisateur sur Angular + PHP puis redirige
   * sendConfirmationEmail = true car vient du mail d'activation (parcours demande)
   *
   * SÉCURITÉ : L'API retourne SUCCESS pour account_activated ET already_active (si token valide)
   * L'auto-login n'est fait que sur SUCCESS
   */
  private activateAndShowConfirmation(): void {
    const username = this.route.snapshot.queryParams.u;
    this.authenticationService.activateAccount(this.token, true)
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.loading = false;
          // Token valide - activation ou déjà actif
          if (res.msg === 'already_active') {
            this.success = 'Votre compte est déjà activé. Redirection en cours...';
            this.isAlreadyActive = true;
          } else {
            this.success = 'Votre compte a été activé avec succès ! Redirection en cours...';
          }
          // Connecter sur PHP et rediriger vers mon-espace
          const email = res.user?.email || username;
          if (email) {
            this.loginPhpAndRedirect(email, '/jeunes/mon-espace');
          } else {
            this.router.navigate(['/jeunes/mon-espace']);
          }
        },
        error: (error) => {
          // Token invalide ou erreur - NE PAS faire d'auto-login (sécurité)
          this.loading = false;
          const msg = error?.msg || error?.message || (typeof error === 'string' ? error : '');
          if (msg === 'invalid_token') {
            this.error = 'Ce lien d\'activation n\'est plus valide ou a expiré.';
          } else {
            this.error = 'Une erreur est survenue lors de l\'activation.';
          }
        }
      });
  }

  /**
   * Redirige vers l'URL (externe ou interne)
   */
  private redirectTo(url: string): void {
    if (url.startsWith('http://') || url.startsWith('https://')) {
      window.location.href = url;
    } else {
      this.router.navigateByUrl(url);
    }
  }

  /**
   * Connecte l'utilisateur sur PHP via login.php puis redirige vers la destination
   */
  private loginPhpAndRedirect(email: string, redirectUrl: string): void {
    // Construire l'URL de redirection finale (Angular)
    const finalRedirect = redirectUrl.startsWith('http')
      ? redirectUrl
      : location.protocol + '//' + location.host + environment.baseHref + redirectUrl.replace(/^\//, '');

    // Rediriger vers login.php avec connexion automatique
    this.redirectService.redirectToIndexWithPostParams(
      environment.cllajUrl + '/login.php',
      {
        email: email,
        password: 'forcePWD2024Condorcet',
        redirect: finalRedirect
      }
    );
  }

  // Convenience getter for request form controls
  get rf() { return this.requestForm?.controls; }

  /**
   * Verify the activation token from URL
   */
  verifyToken(): void {
    this.loading = true;
    this.authenticationService.verifyActivationToken(this.token)
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.loading = false;
          this.user = res.user;
        },
        error: (error) => {
          this.loading = false;
          // L'ErrorInterceptor retourne directement le body
          const msg = error?.msg || error?.message || (typeof error === 'string' ? error : '');
          if (msg === 'invalid_token') {
            this.error = 'Ce lien d\'activation n\'est plus valide ou a expiré. Veuillez demander un nouveau lien.';
          } else if (msg === 'already_validated' || msg === 'already_active') {
            this.error = 'Bonne nouvelle ! Votre compte est déjà activé. Vous pouvez vous connecter directement.';
            this.isAlreadyActive = true;
          } else {
            this.error = 'Une erreur est survenue lors de la vérification. Veuillez réessayer.';
          }
        }
      });
  }

  /**
   * Submit request for activation email
   */
  onSubmitRequest($event: Event): void {
    $event.stopPropagation();
    this.submitted = true;
    this.error = '';
    this.success = '';
    this.isAlreadyActive = false;

    if (this.requestForm.invalid) {
      return;
    }

    this.loading = true;
    this.authenticationService.sendActivationEmail(this.rf.username.value)
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.loading = false;
          alert('Un email d\'activation vous a été envoyé. Merci de cliquer sur le lien présent dans celui-ci pour activer votre compte.', 'Activation du compte');
        },
        error: (error) => {
          this.loading = false;
          // L'ErrorInterceptor retourne directement le body (error = { msg: "..." })
          // ou un string, pas l'HttpErrorResponse complet
          const msg = error?.msg || error?.message || (typeof error === 'string' ? error : '');

          if (msg === 'unexist') {
            this.error = 'Aucun compte n\'est associé à cette adresse email. Veuillez vérifier votre saisie ou créer un compte.';
          } else if (msg === 'already_validated' || msg === 'already_active') {
            this.error = 'Bonne nouvelle ! Votre compte est déjà activé. Vous pouvez vous connecter directement.';
            this.isAlreadyActive = true;
          } else if (msg === 'Email requis' || (typeof msg === 'string' && msg.includes('requis'))) {
            this.error = 'Veuillez saisir votre adresse email.';
          } else {
            this.error = 'Une erreur est survenue. Veuillez réessayer ultérieurement.';
          }
        }
      });
  }

  /**
   * Activate the account (bouton manuel)
   * Connecte l'utilisateur sur Angular + PHP puis redirige
   * sendConfirmationEmail = true si pas de redirect (vient du mail d'activation)
   *
   * SÉCURITÉ : L'API retourne SUCCESS pour account_activated ET already_active (si token valide)
   */
  onActivate(): void {
    this.loading = true;
    this.error = '';
    const hasRedirect = !!this.route.snapshot.queryParams['redirect'];
    const username = this.route.snapshot.queryParams.u;

    this.authenticationService.activateAccount(this.token, !hasRedirect)
      .pipe(first())
      .subscribe({
        next: (res) => {
          this.loading = false;
          // Token valide - afficher le bon message
          if (res.msg === 'already_active') {
            this.success = 'Votre compte est déjà activé. Redirection en cours...';
            this.isAlreadyActive = true;
          }

          // Déterminer la destination
          const redirectUrl = this.route.snapshot.queryParams['redirect'];
          const returnUrl = this.route.snapshot.queryParams['returnUrl'];
          const destination = redirectUrl || returnUrl || '/jeunes/mon-espace';

          // Connecter sur PHP et rediriger
          const email = res.user?.email || username;
          if (email) {
            this.loginPhpAndRedirect(email, destination);
          } else {
            this.redirectTo(destination);
          }
        },
        error: (error) => {
          // Token invalide ou erreur - NE PAS faire d'auto-login (sécurité)
          this.loading = false;
          const msg = error?.msg || error?.message || (typeof error === 'string' ? error : '');
          if (msg === 'invalid_token') {
            this.error = 'Ce lien d\'activation n\'est plus valide. Veuillez demander un nouveau lien.';
          } else {
            this.error = 'Une erreur est survenue lors de l\'activation. Veuillez réessayer.';
          }
        }
      });
  }

  goBack(): void {
    if (window) {
      window.history.back();
    }
  }

  goToLogin(): void {
    const qp = this.requestForm && this.rf?.username?.value
      ? { u: this.rf.username.value }
      : {};
    this.router.navigate(['/auth/login'], { queryParams: qp });
  }

  goToSignin(): void {
    const qp = this.requestForm && this.rf?.username?.value
      ? { u: this.rf.username.value }
      : {};
    this.router.navigate(['/auth/signin'], { queryParams: qp });
  }
}
